import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  RefreshCw, Users, Zap, GitBranch, HardDrive,
  Lock, Database, BarChart3, AlertTriangle, CheckCircle,
  FileX, Activity, Clock, TrendingUp, Table2, X,
} from 'lucide-react';
import { AgGridReact } from 'ag-grid-react';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import type { CellStyle, ColDef, ICellRendererParams } from 'ag-grid-community';
import { fetchPerformanceMetrics, fetchIndexStats, fetchWaitingQueries, fetchLongRunningQueries, fetchTableStats, type PerformanceMetrics, type IndexStatEntry, type WaitingQueryEntry, type LongRunningQueryEntry, type TableStatEntry } from '@/api/monitoring';
import { useErrorToast, LoadRetry } from '@/lib/notify';

// ── helpers ──────────────────────────────────────────────────────────────────
function pct(a: number, b: number): number | null {
  return (a + b) > 0 ? Math.round((a / (a + b)) * 100) : null;
}
function fmt(n: number): string {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}G`;
  if (n >= 1_000_000)     return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)         return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}
function fmtBytes(bytes: number): string {
  if (bytes >= 1 << 30) return `${(bytes / (1 << 30)).toFixed(1)} GB`;
  if (bytes >= 1 << 20) return `${(bytes / (1 << 20)).toFixed(1)} MB`;
  if (bytes >= 1 << 10) return `${(bytes / (1 << 10)).toFixed(1)} KB`;
  return `${bytes} B`;
}
function cs(style: CellStyle): CellStyle {
  return style;
}

// ── Card primitives ───────────────────────────────────────────────────────────
type Status = 'ok' | 'warn' | 'error' | 'info' | 'muted';

const STATUS_COLORS: Record<Status, { card: string; icon: string; value: string }> = {
  ok:    { card: 'border-emerald-100 bg-emerald-50/40',  icon: 'text-emerald-500', value: 'text-emerald-700' },
  warn:  { card: 'border-amber-100   bg-amber-50/40',    icon: 'text-amber-500',   value: 'text-amber-700'   },
  error: { card: 'border-rose-100    bg-rose-50/40',     icon: 'text-rose-500',    value: 'text-rose-700'    },
  info:  { card: 'border-blue-100    bg-blue-50/40',     icon: 'text-blue-500',    value: 'text-blue-700'    },
  muted: { card: 'border-gray-100    bg-gray-50',        icon: 'text-gray-400',    value: 'text-gray-700'    },
};

function MetricCard({
  icon, label, value, sub, status = 'muted', progress, action,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  sub?: string;
  status?: Status;
  progress?: number;
  action?: React.ReactNode;
}) {
  const c = STATUS_COLORS[status];
  return (
    <div className={`rounded-lg border p-2.5 flex flex-col gap-1 ${c.card}`}>
      <div className="flex items-center justify-between">
        <span className={`${c.icon}`}>{icon}</span>
        <div className="flex items-center gap-1">
          {action}
          {status !== 'muted' && (
            <span className={`text-[10px] font-medium px-1 py-0.5 rounded-full ${
              status === 'ok'    ? 'bg-emerald-100 text-emerald-700' :
              status === 'warn'  ? 'bg-amber-100   text-amber-700'   :
              status === 'error' ? 'bg-rose-100    text-rose-700'    :
                                   'bg-blue-100    text-blue-700'
            }`}>
              {status === 'ok' ? 'İyi' : status === 'warn' ? 'Dikkat' : status === 'error' ? 'Kritik' : 'Bilgi'}
            </span>
          )}
        </div>
      </div>
      <div>
        <p className="text-[11px] text-gray-500 leading-tight">{label}</p>
        <p className={`text-sm font-bold leading-snug ${c.value}`}>{value}</p>
        {sub && <p className="text-[10px] text-gray-400 leading-tight mt-0.5">{sub}</p>}
      </div>
      {progress != null && (
        <div className="h-1 rounded-full bg-gray-200 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              progress >= 90 ? 'bg-rose-400' : progress >= 70 ? 'bg-amber-400' : 'bg-emerald-400'
            }`}
            style={{ width: `${Math.min(progress, 100)}%` }}
          />
        </div>
      )}
    </div>
  );
}

// ── Sequential Scan Modal ─────────────────────────────────────────────────────

function SeqScanModal({ onClose }: { onClose: () => void }) {
  const [data, setData] = useState<TableStatEntry[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useErrorToast(error);

  const load = useCallback(() => {
    setLoading(true);
    fetchTableStats()
      .then(setData)
      .catch(() => setError('Veriler yüklenemedi.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const colDefs = useMemo<ColDef<TableStatEntry>[]>(() => [
    {
      headerName: 'Tablo',
      field: 'table_name',
      flex: 2,
      minWidth: 140,
      filter: true,
      cellStyle: cs({ fontSize: '12px', fontWeight: 500, color: '#1f2937' }),
    },
    {
      headerName: 'Seq Scan %',
      width: 130,
      sort: 'desc',
      valueGetter: ({ data: d }) => {
        if (!d) return null;
        const total = d.seq_scan + d.idx_scan;
        return total > 0 ? Math.round((d.seq_scan / total) * 100) : null;
      },
      comparator: (a, b) => Number(a ?? -1) - Number(b ?? -1),
      cellRenderer: ({ value }: ICellRendererParams) => {
        if (value == null) return <span className="text-gray-300 text-xs">—</span>;
        const cls = value >= 50 ? 'text-rose-600' : value >= 20 ? 'text-amber-500' : 'text-emerald-600';
        const bar = value >= 50 ? 'bg-rose-400' : value >= 20 ? 'bg-amber-400' : 'bg-emerald-400';
        return (
          <div className="flex items-center gap-1.5">
            <div className="h-1.5 w-14 rounded-full bg-gray-100 overflow-hidden">
              <div className={`h-full rounded-full ${bar}`} style={{ width: `${value}%` }} />
            </div>
            <span className={`text-xs font-semibold ${cls}`}>%{value}</span>
          </div>
        );
      },
    },
    {
      headerName: 'Seq Scan',
      field: 'seq_scan',
      width: 110,
      comparator: (a, b) => Number(a) - Number(b),
      cellRenderer: ({ value }: ICellRendererParams) => {
        const n = Number(value);
        const cls = n > 10000 ? 'text-rose-500 font-semibold' : n > 1000 ? 'text-amber-500' : 'text-gray-600';
        return <span className={`text-xs font-mono ${cls}`}>{n.toLocaleString('tr-TR')}</span>;
      },
    },
    {
      headerName: 'Idx Scan',
      field: 'idx_scan',
      width: 110,
      comparator: (a, b) => Number(a) - Number(b),
      cellRenderer: ({ value }: ICellRendererParams) => (
        <span className="text-xs font-mono text-emerald-600">{Number(value).toLocaleString('tr-TR')}</span>
      ),
    },
    {
      headerName: 'Satır (live)',
      field: 'row_count',
      width: 110,
      comparator: (a, b) => Number(a) - Number(b),
      cellStyle: cs({ fontSize: '11px', color: '#6b7280', fontFamily: 'monospace' }),
      valueFormatter: ({ value }) => Number(value).toLocaleString('tr-TR'),
    },
    {
      headerName: 'Tablo Boyutu',
      field: 'table_size',
      width: 120,
      comparator: (_, __, nodeA, nodeB) =>
        (nodeA.data?.total_size_bytes ?? 0) - (nodeB.data?.total_size_bytes ?? 0),
      cellStyle: cs({ fontSize: '11px', color: '#6b7280' }),
    },
    {
      headerName: 'Son Analyze',
      width: 140,
      valueGetter: ({ data: d }) => d?.last_analyze ?? d?.last_autoanalyze ?? null,
      cellRenderer: ({ data: d }: ICellRendererParams<TableStatEntry>) => {
        const val = d?.last_analyze ?? d?.last_autoanalyze;
        if (!val) return <span className="text-gray-300 text-xs">—</span>;
        return <span className="text-xs text-gray-500">{new Date(val).toLocaleDateString('tr-TR')}</span>;
      },
    },
  ], []);

  const summary = data ? (() => {
    const highSeq = data.filter(r => {
      const total = r.seq_scan + r.idx_scan;
      return total > 0 && Math.round((r.seq_scan / total) * 100) >= 50;
    }).length;
    return { highSeq };
  })() : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl flex flex-col overflow-hidden" style={{ height: '78vh' }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b bg-gray-50 shrink-0">
          <div className="flex items-center gap-3">
            <Activity size={16} className="text-rose-500" />
            <span className="font-semibold text-gray-800 text-sm">Sequential Scan Dağılımı</span>
            {summary && summary.highSeq > 0 && (
              <span className="text-xs text-gray-400">
                <span className="text-rose-500 font-semibold">{summary.highSeq} tablo</span> &gt;%50 seq scan
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 transition-colors">
              <RefreshCw size={12} /> Yenile
            </button>
            <button onClick={onClose} className="rounded-full p-1.5 hover:bg-gray-200 transition-colors"><X size={16} /></button>
          </div>
        </div>
        <div className="flex-1 min-h-0 ag-theme-quartz">
          {loading && <div className="flex h-full items-center justify-center text-gray-400 text-sm">Yükleniyor...</div>}
          {!loading && data && (
            <AgGridReact<TableStatEntry>
              modules={[AllEnterpriseModule]}
              rowData={data}
              columnDefs={colDefs}
              defaultColDef={{ sortable: true, resizable: true, cellStyle: cs({ paddingTop: 0, paddingBottom: 0, paddingLeft: '8px', paddingRight: '8px' }) }}
              rowHeight={36}
              suppressHorizontalScroll={false}
              tooltipShowDelay={300}
              getRowStyle={({ data: d }) => {
                if (!d) return undefined;
                const total = d.seq_scan + d.idx_scan;
                if (total === 0) return undefined;
                const seqPct = Math.round((d.seq_scan / total) * 100);
                if (seqPct >= 50) return { background: '#fff1f2' };
                if (seqPct >= 20) return { background: '#fffbeb' };
                return undefined;
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Dead Tuple Modal ──────────────────────────────────────────────────────────

function DeadTupleModal({ onClose }: { onClose: () => void }) {
  const [data, setData] = useState<TableStatEntry[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useErrorToast(error);

  const load = useCallback(() => {
    setLoading(true);
    fetchTableStats()
      .then(setData)
      .catch(() => setError('Veriler yüklenemedi.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const colDefs = useMemo<ColDef<TableStatEntry>[]>(() => [
    {
      headerName: 'Tablo',
      field: 'table_name',
      flex: 2,
      minWidth: 140,
      filter: true,
      cellStyle: cs({ fontSize: '12px', fontWeight: 500, color: '#1f2937' }),
    },
    {
      headerName: 'Dead Tuple %',
      width: 130,
      sort: 'desc',
      valueGetter: ({ data: d }) => {
        if (!d) return null;
        const total = d.row_count + d.dead_rows;
        return total > 0 ? Math.round((d.dead_rows / total) * 100) : null;
      },
      comparator: (a, b) => Number(a ?? -1) - Number(b ?? -1),
      cellRenderer: ({ value }: ICellRendererParams) => {
        if (value == null) return <span className="text-gray-300 text-xs">—</span>;
        const cls = value >= 20 ? 'text-rose-600' : value >= 10 ? 'text-amber-500' : 'text-emerald-600';
        const bar = value >= 20 ? 'bg-rose-400' : value >= 10 ? 'bg-amber-400' : 'bg-emerald-400';
        return (
          <div className="flex items-center gap-1.5">
            <div className="h-1.5 w-14 rounded-full bg-gray-100 overflow-hidden">
              <div className={`h-full rounded-full ${bar}`} style={{ width: `${Math.min(value, 100)}%` }} />
            </div>
            <span className={`text-xs font-semibold ${cls}`}>%{value}</span>
          </div>
        );
      },
    },
    {
      headerName: 'Dead Rows',
      field: 'dead_rows',
      width: 110,
      comparator: (a, b) => Number(a) - Number(b),
      cellRenderer: ({ value }: ICellRendererParams) => {
        const n = Number(value);
        const cls = n > 10000 ? 'text-rose-500 font-semibold' : n > 1000 ? 'text-amber-500' : 'text-gray-500';
        return <span className={`text-xs font-mono ${cls}`}>{n.toLocaleString('tr-TR')}</span>;
      },
    },
    {
      headerName: 'Live Rows',
      field: 'row_count',
      width: 110,
      comparator: (a, b) => Number(a) - Number(b),
      cellRenderer: ({ value }: ICellRendererParams) => (
        <span className="text-xs font-mono text-emerald-600">{Number(value).toLocaleString('tr-TR')}</span>
      ),
    },
    {
      headerName: 'HOT Update %',
      width: 130,
      valueGetter: ({ data: d }) => {
        if (!d || d.n_tup_upd === 0) return null;
        return Math.round((d.n_tup_hot_upd / d.n_tup_upd) * 100);
      },
      comparator: (a, b) => Number(a ?? -1) - Number(b ?? -1),
      cellRenderer: ({ value }: ICellRendererParams) => {
        if (value == null) return <span className="text-gray-300 text-xs">—</span>;
        const cls = value >= 80 ? 'text-emerald-600' : value >= 50 ? 'text-amber-500' : 'text-rose-500';
        return <span className={`text-xs font-semibold ${cls}`}>%{value}</span>;
      },
    },
    {
      headerName: 'Tablo Boyutu',
      field: 'table_size',
      width: 120,
      comparator: (_, __, nodeA, nodeB) =>
        (nodeA.data?.total_size_bytes ?? 0) - (nodeB.data?.total_size_bytes ?? 0),
      cellStyle: cs({ fontSize: '11px', color: '#6b7280' }),
    },
    {
      headerName: 'Son Vacuum',
      width: 140,
      valueGetter: ({ data: d }) => d?.last_vacuum ?? d?.last_autovacuum ?? null,
      comparator: (a, b) => (a ?? '').localeCompare(b ?? ''),
      cellRenderer: ({ data: d }: ICellRendererParams<TableStatEntry>) => {
        const val = d?.last_vacuum ?? d?.last_autovacuum;
        if (!val) return <span className="text-rose-400 text-xs font-medium">Hiç yapılmamış</span>;
        const auto = !d?.last_vacuum && !!d?.last_autovacuum;
        return (
          <span className="text-xs text-gray-500">
            {new Date(val).toLocaleDateString('tr-TR')}
            {auto && <span className="ml-1 text-[10px] text-gray-400">(auto)</span>}
          </span>
        );
      },
    },
    {
      headerName: 'Son Analyze',
      width: 140,
      valueGetter: ({ data: d }) => d?.last_analyze ?? d?.last_autoanalyze ?? null,
      comparator: (a, b) => (a ?? '').localeCompare(b ?? ''),
      cellRenderer: ({ data: d }: ICellRendererParams<TableStatEntry>) => {
        const val = d?.last_analyze ?? d?.last_autoanalyze;
        if (!val) return <span className="text-gray-300 text-xs">—</span>;
        return <span className="text-xs text-gray-500">{new Date(val).toLocaleDateString('tr-TR')}</span>;
      },
    },
  ], []);

  const summary = data ? (() => {
    const highDead = data.filter(r => {
      const total = r.row_count + r.dead_rows;
      return total > 0 && Math.round((r.dead_rows / total) * 100) >= 10;
    }).length;
    const totalDead = data.reduce((s, r) => s + Number(r.dead_rows), 0);
    return { highDead, totalDead };
  })() : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl flex flex-col overflow-hidden" style={{ height: '78vh' }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b bg-gray-50 shrink-0">
          <div className="flex items-center gap-3">
            <AlertTriangle size={16} className="text-orange-500" />
            <span className="font-semibold text-gray-800 text-sm">Dead Tuple Dağılımı</span>
            {summary && (
              <span className="text-xs text-gray-400">
                Toplam <span className="font-semibold text-gray-600">{summary.totalDead.toLocaleString('tr-TR')}</span> ölü satır
                {summary.highDead > 0 && <> · <span className="text-rose-500 font-semibold">{summary.highDead} tablo</span> &gt;%10</>}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 transition-colors">
              <RefreshCw size={12} /> Yenile
            </button>
            <button onClick={onClose} className="rounded-full p-1.5 hover:bg-gray-200 transition-colors"><X size={16} /></button>
          </div>
        </div>
        <div className="flex-1 min-h-0 ag-theme-quartz">
          {loading && <div className="flex h-full items-center justify-center text-gray-400 text-sm">Yükleniyor...</div>}
          {!loading && data && (
            <AgGridReact<TableStatEntry>
              modules={[AllEnterpriseModule]}
              rowData={data}
              columnDefs={colDefs}
              defaultColDef={{ sortable: true, resizable: true, cellStyle: cs({ paddingTop: 0, paddingBottom: 0, paddingLeft: '8px', paddingRight: '8px' }) }}
              rowHeight={36}
              suppressHorizontalScroll={false}
              tooltipShowDelay={300}
              getRowStyle={({ data: d }) => {
                if (!d) return undefined;
                const total = d.row_count + d.dead_rows;
                if (total === 0) return undefined;
                const pctVal = Math.round((d.dead_rows / total) * 100);
                if (pctVal >= 20) return { background: '#fff1f2' };
                if (pctVal >= 10) return { background: '#fffbeb' };
                return undefined;
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Cache Hit Detail Modal ────────────────────────────────────────────────────

type CacheRow = TableStatEntry & { idx_blks_hit_total: number; idx_blks_read_total: number };

function cacheHitPctOf(hit: number, read: number): number | null {
  return hit + read > 0 ? Math.round((hit / (hit + read)) * 100) : null;
}

function CacheHitBar({ hit, read }: { hit: number; read: number }) {
  const pctVal = cacheHitPctOf(hit, read);
  if (pctVal == null) return <span className="text-gray-300 text-xs">—</span>;
  const cls = pctVal >= 95 ? 'text-emerald-600' : pctVal >= 80 ? 'text-amber-500' : 'text-rose-500';
  const bar = pctVal >= 95 ? 'bg-emerald-400' : pctVal >= 80 ? 'bg-amber-400' : 'bg-rose-400';
  return (
    <div className="flex items-center gap-1.5">
      <div className="h-1.5 w-14 rounded-full bg-gray-100 overflow-hidden">
        <div className={`h-full rounded-full ${bar}`} style={{ width: `${pctVal}%` }} />
      </div>
      <span className={`text-xs font-semibold ${cls}`}>%{pctVal}</span>
    </div>
  );
}

function CacheHitModal({ onClose }: { onClose: () => void }) {
  const [data, setData] = useState<CacheRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useErrorToast(error);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([fetchTableStats(), fetchIndexStats()])
      .then(([tables, indexes]) => {
        // tablo adına göre index cache hit toplamları
        const idxMap = new Map<string, { hit: number; read: number }>();
        for (const idx of indexes) {
          const key = idx.table_name;
          const prev = idxMap.get(key) ?? { hit: 0, read: 0 };
          idxMap.set(key, {
            hit:  prev.hit  + (idx.idx_blks_hit  ?? 0),
            read: prev.read + (idx.idx_blks_read  ?? 0),
          });
        }
        const merged: CacheRow[] = tables.map(t => ({
          ...t,
          idx_blks_hit_total:  idxMap.get(t.table_name)?.hit  ?? 0,
          idx_blks_read_total: idxMap.get(t.table_name)?.read ?? 0,
        }));
        setData(merged);
      })
      .catch(() => setError('Veriler yüklenemedi.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const colDefs = useMemo<ColDef<CacheRow>[]>(() => [
    {
      headerName: 'Tablo',
      field: 'table_name',
      flex: 2,
      minWidth: 140,
      filter: true,
      cellStyle: cs({ fontSize: '12px', fontWeight: 500, color: '#1f2937' }),
    },
    {
      headerName: 'Heap Cache %',
      width: 130,
      sort: 'asc',
      valueGetter: ({ data: d }) => d ? cacheHitPctOf(d.heap_blks_hit, d.heap_blks_read) : null,
      comparator: (a, b) => Number(a ?? -1) - Number(b ?? -1),
      cellRenderer: ({ data: d }: ICellRendererParams<CacheRow>) =>
        d ? <CacheHitBar hit={d.heap_blks_hit} read={d.heap_blks_read} /> : null,
    },
    {
      headerName: 'Heap Hit',
      field: 'heap_blks_hit',
      width: 95,
      comparator: (a, b) => Number(a) - Number(b),
      cellRenderer: ({ value }: ICellRendererParams) => (
        <span className="text-xs font-mono text-emerald-600">{fmt(Number(value))}</span>
      ),
    },
    {
      headerName: 'Heap Read',
      field: 'heap_blks_read',
      width: 95,
      comparator: (a, b) => Number(a) - Number(b),
      cellRenderer: ({ value }: ICellRendererParams) => {
        const n = Number(value);
        const cls = n > 1000 ? 'text-rose-500 font-semibold' : n > 0 ? 'text-amber-500' : 'text-gray-400';
        return <span className={`text-xs font-mono ${cls}`}>{fmt(n)}</span>;
      },
    },
    {
      headerName: 'Index Cache %',
      width: 130,
      valueGetter: ({ data: d }) => d ? cacheHitPctOf(d.idx_blks_hit_total, d.idx_blks_read_total) : null,
      comparator: (a, b) => Number(a ?? -1) - Number(b ?? -1),
      cellRenderer: ({ data: d }: ICellRendererParams<CacheRow>) =>
        d ? <CacheHitBar hit={d.idx_blks_hit_total} read={d.idx_blks_read_total} /> : null,
    },
    {
      headerName: 'Idx Hit',
      field: 'idx_blks_hit_total',
      width: 90,
      comparator: (a, b) => Number(a) - Number(b),
      cellRenderer: ({ value }: ICellRendererParams) => (
        <span className="text-xs font-mono text-emerald-600">{fmt(Number(value))}</span>
      ),
    },
    {
      headerName: 'Idx Read',
      field: 'idx_blks_read_total',
      width: 90,
      comparator: (a, b) => Number(a) - Number(b),
      cellRenderer: ({ value }: ICellRendererParams) => {
        const n = Number(value);
        const cls = n > 1000 ? 'text-rose-500 font-semibold' : n > 0 ? 'text-amber-500' : 'text-gray-400';
        return <span className={`text-xs font-mono ${cls}`}>{fmt(n)}</span>;
      },
    },
    {
      headerName: 'Satır',
      field: 'row_count',
      width: 90,
      comparator: (a, b) => Number(a) - Number(b),
      cellStyle: cs({ fontSize: '11px', color: '#6b7280', fontFamily: 'monospace' }),
      valueFormatter: ({ value }) => Number(value).toLocaleString('tr-TR'),
    },
    {
      headerName: 'Boyut',
      field: 'table_size',
      width: 100,
      comparator: (_, __, nodeA, nodeB) =>
        (nodeA.data?.total_size_bytes ?? 0) - (nodeB.data?.total_size_bytes ?? 0),
      cellStyle: cs({ fontSize: '11px', color: '#6b7280' }),
    },
    {
      headerName: 'Seq Scan',
      field: 'seq_scan',
      width: 90,
      comparator: (a, b) => Number(a) - Number(b),
      cellRenderer: ({ value }: ICellRendererParams) => {
        const n = Number(value);
        const cls = n > 10000 ? 'text-rose-500 font-semibold' : 'text-gray-600';
        return <span className={`text-xs font-mono ${cls}`}>{fmt(n)}</span>;
      },
    },
    {
      headerName: 'Idx Scan',
      field: 'idx_scan',
      width: 90,
      comparator: (a, b) => Number(a) - Number(b),
      cellRenderer: ({ value }: ICellRendererParams) => (
        <span className="text-xs font-mono text-gray-600">{fmt(Number(value))}</span>
      ),
    },
  ], []);

  const summary = data ? (() => {
    const totalHit  = data.reduce((s, r) => s + r.heap_blks_hit + r.idx_blks_hit_total, 0);
    const totalRead = data.reduce((s, r) => s + r.heap_blks_read + r.idx_blks_read_total, 0);
    const overall = cacheHitPctOf(totalHit, totalRead);
    const poorCount = data.filter(r => {
      const hp = cacheHitPctOf(r.heap_blks_hit, r.heap_blks_read);
      const ip = cacheHitPctOf(r.idx_blks_hit_total, r.idx_blks_read_total);
      return (hp != null && hp < 80) || (ip != null && ip < 80);
    }).length;
    return { overall, poorCount };
  })() : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl flex flex-col overflow-hidden"
        style={{ height: '78vh' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b bg-gray-50 shrink-0">
          <div className="flex items-center gap-3">
            <BarChart3 size={16} className="text-blue-500" />
            <span className="font-semibold text-gray-800 text-sm">Cache Hit Oranları (Heap + Index)</span>
            {summary && (
              <span className="text-xs text-gray-400">
                Genel: <span className={`font-semibold ${summary.overall != null && summary.overall >= 95 ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {summary.overall != null ? `%${summary.overall}` : '—'}
                </span>
                {summary.poorCount > 0 && (
                  <> · <span className="text-rose-500 font-semibold">{summary.poorCount} tablo</span> &lt;%80</>
                )}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <RefreshCw size={12} /> Yenile
            </button>
            <button onClick={onClose} className="rounded-full p-1.5 hover:bg-gray-200 transition-colors">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 ag-theme-quartz">
          {loading && <div className="flex h-full items-center justify-center text-gray-400 text-sm">Yükleniyor...</div>}
          {!loading && data && (
            <AgGridReact<CacheRow>
              modules={[AllEnterpriseModule]}
              rowData={data}
              columnDefs={colDefs}
              defaultColDef={{
                sortable: true,
                resizable: true,
                cellStyle: cs({ paddingTop: 0, paddingBottom: 0, paddingLeft: '8px', paddingRight: '8px' }),
              }}
              rowHeight={36}
              suppressHorizontalScroll={false}
              tooltipShowDelay={300}
              getRowStyle={({ data: d }) => {
                if (!d) return undefined;
                const hp = cacheHitPctOf(d.heap_blks_hit, d.heap_blks_read);
                const ip = cacheHitPctOf(d.idx_blks_hit_total, d.idx_blks_read_total);
                const worst = Math.min(hp ?? 100, ip ?? 100);
                if (worst < 80) return { background: '#fff1f2' };
                if (worst < 95) return { background: '#fffbeb' };
                return undefined;
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Long Running Queries Modal ────────────────────────────────────────────────

function LongRunningQueriesModal({ onClose }: { onClose: () => void }) {
  const [data, setData] = useState<LongRunningQueryEntry[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useErrorToast(error);

  const load = useCallback(() => {
    setLoading(true);
    fetchLongRunningQueries()
      .then(setData)
      .catch(() => setError('Veriler yüklenemedi.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const colDefs = useMemo<ColDef<LongRunningQueryEntry>[]>(() => [
    {
      headerName: 'PID',
      field: 'pid',
      width: 80,
      cellStyle: cs({ fontSize: '11px', fontFamily: 'monospace' }),
    },
    {
      headerName: 'Süre',
      field: 'duration_s',
      width: 90,
      sort: 'desc',
      comparator: (a, b) => Number(a) - Number(b),
      cellRenderer: ({ value }: ICellRendererParams) => {
        const n = Number(value);
        const cls = n >= 300 ? 'text-rose-600 font-bold' : n >= 60 ? 'text-amber-600 font-semibold' : 'text-gray-600';
        const label = n >= 60 ? `${(n / 60).toFixed(1)}dk` : `${n.toFixed(1)}s`;
        return <span className={`text-xs font-mono ${cls}`}>{label}</span>;
      },
    },
    {
      headerName: 'Durum',
      field: 'state',
      width: 110,
      filter: true,
      cellRenderer: ({ value }: ICellRendererParams) => {
        const cls = value === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600';
        return <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${cls}`}>{value}</span>;
      },
    },
    {
      headerName: 'Bekleme',
      width: 120,
      valueGetter: ({ data: d }) => d ? (d.wait_event_type ?? '—') : '—',
      filter: true,
      cellRenderer: ({ data: d }: ICellRendererParams<LongRunningQueryEntry>) => {
        if (!d?.wait_event_type) return <span className="text-gray-300 text-xs">—</span>;
        return (
          <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded text-[10px] font-semibold">
            {d.wait_event_type}{d.wait_event ? `: ${d.wait_event}` : ''}
          </span>
        );
      },
    },
    {
      headerName: 'Kullanıcı',
      field: 'usename',
      width: 110,
      filter: true,
      cellStyle: cs({ fontSize: '11px' }),
    },
    {
      headerName: 'Uygulama',
      field: 'application_name',
      width: 130,
      filter: true,
      cellStyle: cs({ fontSize: '11px', color: '#6b7280' }),
      valueFormatter: ({ value }) => value || '—',
    },
    {
      headerName: 'Bloklayan',
      field: 'blocking_pids',
      width: 110,
      cellRenderer: ({ value }: ICellRendererParams) => {
        const pids: number[] = value ?? [];
        if (pids.length === 0) return <span className="text-gray-300 text-xs">—</span>;
        return <span className="text-rose-600 text-xs font-mono font-semibold">{pids.join(', ')}</span>;
      },
    },
    {
      headerName: 'Sorgu',
      field: 'query',
      flex: 1,
      minWidth: 200,
      cellStyle: cs({ fontSize: '10px', fontFamily: 'monospace', color: '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
      tooltipField: 'query',
    },
  ], []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl flex flex-col overflow-hidden"
        style={{ height: '75vh' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b bg-gray-50 shrink-0">
          <div className="flex items-center gap-3">
            <AlertTriangle size={16} className="text-rose-500" />
            <span className="font-semibold text-gray-800 text-sm">Uzun Çalışan Sorgular</span>
            {data && (
              <span className="text-xs text-gray-400">
                {data.length === 0
                  ? 'Uzun çalışan sorgu yok'
                  : <><span className="font-semibold text-rose-600">{data.length}</span> sorgu (&gt;5s)</>
                }
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <RefreshCw size={12} /> Yenile
            </button>
            <button onClick={onClose} className="rounded-full p-1.5 hover:bg-gray-200 transition-colors">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 ag-theme-quartz">
          {loading && <div className="flex h-full items-center justify-center text-gray-400 text-sm">Yükleniyor...</div>}
          {!loading && data && data.length === 0 && (
            <div className="flex h-full items-center justify-center text-emerald-600 text-sm">
              ✓ Uzun çalışan sorgu bulunamadı.
            </div>
          )}
          {!loading && data && data.length > 0 && (
            <AgGridReact<LongRunningQueryEntry>
              modules={[AllEnterpriseModule]}
              rowData={data}
              columnDefs={colDefs}
              defaultColDef={{
                sortable: true,
                resizable: true,
                cellStyle: cs({ paddingTop: 0, paddingBottom: 0, paddingLeft: '8px', paddingRight: '8px' }),
              }}
              rowHeight={36}
              suppressHorizontalScroll={false}
              tooltipShowDelay={300}
              getRowStyle={({ data: d }) => {
                if (!d) return undefined;
                if ((d.blocking_pids?.length ?? 0) > 0) return { background: '#fff1f2' };
                if (d.duration_s >= 300) return { background: '#fff7ed' };
                return undefined;
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Waiting Queries Modal ─────────────────────────────────────────────────────

function WaitingQueriesModal({ onClose }: { onClose: () => void }) {
  const [data, setData] = useState<WaitingQueryEntry[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useErrorToast(error);

  const load = useCallback(() => {
    setLoading(true);
    fetchWaitingQueries()
      .then(setData)
      .catch(() => setError('Veriler yüklenemedi.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const colDefs = useMemo<ColDef<WaitingQueryEntry>[]>(() => [
    {
      headerName: 'PID',
      field: 'pid',
      width: 80,
      cellStyle: cs({ fontSize: '11px', fontFamily: 'monospace' }),
    },
    {
      headerName: 'Süre (s)',
      field: 'duration_s',
      width: 90,
      sort: 'desc',
      comparator: (a, b) => Number(a) - Number(b),
      cellRenderer: ({ value }: ICellRendererParams) => {
        const n = Number(value);
        const cls = n >= 30 ? 'text-rose-600 font-bold' : n >= 5 ? 'text-amber-600 font-semibold' : 'text-gray-600';
        return <span className={`text-xs font-mono ${cls}`}>{n.toFixed(1)}s</span>;
      },
    },
    {
      headerName: 'Bekleme Tipi',
      field: 'wait_event_type',
      width: 120,
      filter: true,
      cellRenderer: ({ value }: ICellRendererParams) => (
        <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded text-[10px] font-semibold">{value}</span>
      ),
    },
    {
      headerName: 'Bekleme Olayı',
      field: 'wait_event',
      width: 140,
      filter: true,
      cellStyle: cs({ fontSize: '11px', color: '#6b7280' }),
    },
    {
      headerName: 'Kullanıcı',
      field: 'usename',
      width: 110,
      filter: true,
      cellStyle: cs({ fontSize: '11px' }),
    },
    {
      headerName: 'Uygulama',
      field: 'application_name',
      width: 130,
      filter: true,
      cellStyle: cs({ fontSize: '11px', color: '#6b7280' }),
      valueFormatter: ({ value }) => value || '—',
    },
    {
      headerName: 'Bloklayan PID',
      field: 'blocking_pids',
      width: 130,
      cellRenderer: ({ value }: ICellRendererParams) => {
        const pids: number[] = value ?? [];
        if (pids.length === 0) return <span className="text-gray-300 text-xs">—</span>;
        return (
          <span className="text-rose-600 text-xs font-mono font-semibold">
            {pids.join(', ')}
          </span>
        );
      },
    },
    {
      headerName: 'Sorgu',
      field: 'query',
      flex: 1,
      minWidth: 200,
      cellStyle: cs({ fontSize: '10px', fontFamily: 'monospace', color: '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
      tooltipField: 'query',
    },
  ], []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl flex flex-col overflow-hidden"
        style={{ height: '75vh' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b bg-gray-50 shrink-0">
          <div className="flex items-center gap-3">
            <Clock size={16} className="text-amber-500" />
            <span className="font-semibold text-gray-800 text-sm">Bekleyen Sorgular</span>
            {data && (
              <span className="text-xs text-gray-400">
                {data.length === 0
                  ? 'Bekleyen sorgu yok'
                  : <><span className="font-semibold text-amber-600">{data.length}</span> bekleyen sorgu</>
                }
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <RefreshCw size={12} /> Yenile
            </button>
            <button onClick={onClose} className="rounded-full p-1.5 hover:bg-gray-200 transition-colors">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 ag-theme-quartz">
          {loading && <div className="flex h-full items-center justify-center text-gray-400 text-sm">Yükleniyor...</div>}
          {!loading && data && data.length === 0 && (
            <div className="flex h-full items-center justify-center text-emerald-600 text-sm">
              ✓ Bekleyen sorgu bulunamadı.
            </div>
          )}
          {!loading && data && data.length > 0 && (
            <AgGridReact<WaitingQueryEntry>
              modules={[AllEnterpriseModule]}
              rowData={data}
              columnDefs={colDefs}
              defaultColDef={{
                sortable: true,
                resizable: true,
                cellStyle: cs({ paddingTop: 0, paddingBottom: 0, paddingLeft: '8px', paddingRight: '8px' }),
              }}
              rowHeight={36}
              suppressHorizontalScroll={false}
              tooltipShowDelay={300}
              getRowStyle={({ data: d }) => {
                if (!d) return undefined;
                if ((d.blocking_pids?.length ?? 0) > 0) return { background: '#fff1f2' };
                if (d.duration_s >= 30) return { background: '#fff7ed' };
                return undefined;
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Unused Index Modal ────────────────────────────────────────────────────────

function IndexSizeCell({ data }: ICellRendererParams<IndexStatEntry>) {
  if (!data) return null;
  const unused = data.idx_scan === 0;
  return <span className={`text-xs font-semibold ${unused ? 'text-rose-600' : 'text-gray-700'}`}>{data.index_size}</span>;
}

function IndexTypeCell({ data }: ICellRendererParams<IndexStatEntry>) {
  if (!data) return null;
  return (
    <div className="flex items-center gap-1">
      <span className="bg-gray-100 text-gray-600 px-1 py-0.5 rounded text-[10px] uppercase font-mono">{data.index_type}</span>
      {data.is_primary && <span className="bg-blue-100 text-blue-700 px-1 py-0.5 rounded text-[10px] font-semibold">PK</span>}
      {!data.is_primary && data.is_unique && <span className="bg-purple-100 text-purple-700 px-1 py-0.5 rounded text-[10px] font-semibold">UQ</span>}
    </div>
  );
}

function TableNameCell({ data }: ICellRendererParams<IndexStatEntry>) {
  if (!data) return null;
  return <span className="text-xs font-medium text-gray-800">{data.table_name}</span>;
}

function IndexNameCell({ data }: ICellRendererParams<IndexStatEntry>) {
  if (!data) return null;
  const unused = data.idx_scan === 0 && !data.is_primary;
  return <span className={`text-xs font-mono ${unused ? 'text-amber-700' : 'text-gray-700'}`}>{data.index_name}</span>;
}

function NumCell({ value }: ICellRendererParams<IndexStatEntry>) {
  return <span className="text-xs font-mono text-gray-600">{Number(value).toLocaleString('tr-TR')}</span>;
}

function IdxScanCell({ value, data }: ICellRendererParams<IndexStatEntry>) {
  if (!data) return null;
  const n = Number(value);
  const cls = n === 0 && !data.is_primary ? 'text-rose-500 font-semibold' : n > 1000 ? 'text-emerald-600 font-semibold' : 'text-gray-600';
  return <span className={`text-xs font-mono ${cls}`}>{n.toLocaleString('tr-TR')}</span>;
}

function DetailCell({ data }: ICellRendererParams<IndexStatEntry>) {
  if (!data) return null;
  const unused = data.idx_scan === 0 && !data.is_primary;
  const cacheHitPct = (data.idx_blks_hit + data.idx_blks_read) > 0
    ? Math.round((data.idx_blks_hit / (data.idx_blks_hit + data.idx_blks_read)) * 100)
    : null;
  return (
    <div className="py-2 px-3 space-y-1.5 text-xs text-gray-600 bg-gray-50">
      <div className="font-mono text-gray-700 bg-white border rounded px-2 py-1.5 break-all leading-relaxed">
        {data.index_def}
      </div>
      <div className="grid grid-cols-4 gap-x-6 gap-y-0.5 text-gray-500">
        <span>idx_scan: <b>{Number(data.idx_scan).toLocaleString('tr-TR')}</b></span>
        <span>idx_tup_read: <b>{fmt(data.idx_tup_read)}</b></span>
        <span>idx_tup_fetch: <b>{fmt(data.idx_tup_fetch)}</b></span>
        <span>Cache hit: <b>{cacheHitPct != null ? `%${cacheHitPct}` : '—'}</b></span>
        <span>idx_blks_hit: <b>{fmt(data.idx_blks_hit)}</b></span>
        <span>idx_blks_read: <b>{fmt(data.idx_blks_read)}</b></span>
        <span>Ölü satır: <b>{fmt(data.table_dead_rows)}</b></span>
        <span>Index boyutu: <b>{data.index_size}</b></span>
      </div>
      {unused && (
        <p className="text-[10px] text-amber-700">
          ⚠ Bu index hiç kullanılmamış. &nbsp;
          <code className="bg-amber-100 px-1 rounded">DROP INDEX {data.schema}.{data.index_name};</code>
        </p>
      )}
    </div>
  );
}

function UnusedIndexModal({ onClose }: { onClose: () => void }) {
  const gridRef = useRef<AgGridReact<IndexStatEntry>>(null);
  const [data, setData] = useState<IndexStatEntry[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useErrorToast(error);
  const [unusedOnly, setUnusedOnly] = useState(true);

  useEffect(() => {
    setLoading(true);
    setData(null);
    fetchIndexStats(unusedOnly)
      .then(setData)
      .catch(() => setError('Veriler yüklenemedi.'))
      .finally(() => setLoading(false));
  }, [unusedOnly]);

  const totalBytes = data?.reduce((s, r) => s + Number(r.index_size_bytes), 0) ?? 0;
  const unusedCount = data?.filter(r => r.idx_scan === 0 && !r.is_primary).length ?? 0;

  const colDefs = useMemo<ColDef<IndexStatEntry>[]>(() => [
    {
      headerName: 'Tablo',
      field: 'table_name',
      flex: 2,
      filter: true,
      cellRenderer: TableNameCell,
    },
    {
      headerName: 'Index Adı',
      field: 'index_name',
      flex: 2,
      filter: true,
      cellRenderer: IndexNameCell,
    },
    {
      headerName: 'Tip',
      field: 'index_type',
      width: 110,
      filter: true,
      cellRenderer: IndexTypeCell,
    },
    {
      headerName: 'Kolonlar',
      field: 'columns',
      flex: 2,
      filter: true,
      cellStyle: cs({ fontSize: '11px', color: '#6b7280' }),
      valueFormatter: ({ value }) => value ?? '—',
    },
    {
      headerName: 'Index Boyutu',
      field: 'index_size',
      width: 120,
      sort: 'desc',
      comparator: (_, __, nodeA, nodeB) =>
        (nodeA.data?.index_size_bytes ?? 0) - (nodeB.data?.index_size_bytes ?? 0),
      cellRenderer: IndexSizeCell,
    },
    {
      headerName: 'Tablo Boyutu',
      field: 'table_size',
      width: 120,
      comparator: (_, __, nodeA, nodeB) =>
        (nodeA.data?.table_size_bytes ?? 0) - (nodeB.data?.table_size_bytes ?? 0),
      cellStyle: cs({ fontSize: '11px', color: '#6b7280' }),
    },
    {
      headerName: 'Satır',
      field: 'table_live_rows',
      width: 100,
      comparator: (a, b) => Number(a) - Number(b),
      cellRenderer: NumCell,
    },
    {
      headerName: 'idx_scan',
      field: 'idx_scan',
      width: 100,
      comparator: (a, b) => Number(a) - Number(b),
      cellRenderer: IdxScanCell,
    },
    {
      headerName: 'idx_tup_read',
      field: 'idx_tup_read',
      width: 110,
      comparator: (a, b) => Number(a) - Number(b),
      cellRenderer: NumCell,
    },
    {
      headerName: 'Cache Hit %',
      width: 110,
      valueGetter: ({ data: d }) => {
        if (!d) return null;
        const total = d.idx_blks_hit + d.idx_blks_read;
        return total > 0 ? Math.round((d.idx_blks_hit / total) * 100) : null;
      },
      comparator: (a, b) => Number(a ?? -1) - Number(b ?? -1),
      cellRenderer: ({ value }: ICellRendererParams) => {
        if (value == null) return <span className="text-gray-300 text-xs">—</span>;
        const cls = value >= 95 ? 'text-emerald-600' : value >= 80 ? 'text-amber-500' : 'text-rose-500';
        return <span className={`text-xs font-semibold ${cls}`}>%{value}</span>;
      },
    },
  ], []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl flex flex-col overflow-hidden"
        style={{ height: '82vh' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b bg-gray-50 shrink-0">
          <div className="flex items-center gap-3">
            <Table2 size={16} className="text-amber-500" />
            <span className="font-semibold text-gray-800 text-sm">Index İstatistikleri</span>
            {data && (
              <span className="text-xs text-gray-400">
                {data.length} index
                {unusedOnly
                  ? <> · toplam <span className="font-semibold text-rose-500">{fmtBytes(totalBytes)}</span> boşa harcanan alan</>
                  : <> · {unusedCount > 0 && <span className="text-amber-600 font-semibold">{unusedCount} kullanılmayan</span>}</>
                }
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Toggle */}
            <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs">
              <button
                onClick={() => setUnusedOnly(true)}
                className={`px-3 py-1.5 transition-colors ${unusedOnly ? 'bg-amber-500 text-white font-medium' : 'hover:bg-gray-50 text-gray-600'}`}
              >
                Kullanılmayan
              </button>
              <button
                onClick={() => setUnusedOnly(false)}
                className={`px-3 py-1.5 transition-colors border-l border-gray-200 ${!unusedOnly ? 'bg-blue-500 text-white font-medium' : 'hover:bg-gray-50 text-gray-600'}`}
              >
                Tüm Indexler
              </button>
            </div>
            <button onClick={onClose} className="rounded-full p-1.5 hover:bg-gray-200 transition-colors ml-1">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Grid */}
        <div className="flex-1 min-h-0 ag-theme-quartz">
          {loading && (
            <div className="flex h-full items-center justify-center text-gray-400 text-sm">Yükleniyor...</div>
          )}
          {!loading && data && data.length === 0 && (
            <div className="flex h-full items-center justify-center text-emerald-600 text-sm">
              ✓ Kullanılmayan index bulunamadı.
            </div>
          )}
          {!loading && data && data.length > 0 && (
            <AgGridReact<IndexStatEntry>
              modules={[AllEnterpriseModule]}
              ref={gridRef}
              rowData={data}
              columnDefs={colDefs}
              defaultColDef={{
                sortable: true,
                resizable: true,
                cellStyle: cs({ paddingTop: 0, paddingBottom: 0, paddingLeft: '8px', paddingRight: '8px' }),
              }}
              rowHeight={36}
              suppressHorizontalScroll={false}
              tooltipShowDelay={300}
              masterDetail
              detailRowAutoHeight
              detailCellRenderer={DetailCell}
              isRowMaster={() => true}
              getRowStyle={({ data: d }) => {
                if (!d) return undefined;
                if (d.idx_scan === 0 && !d.is_primary) return { background: '#fff7ed' };
                return undefined;
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 border-b pb-1">
        {icon}
        {title}
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
        {children}
      </div>
    </div>
  );
}

function Panel({
  title,
  icon,
  subtitle,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white/80 p-3 sm:p-4 shadow-sm space-y-4">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 rounded-lg bg-blue-50 text-blue-600 p-1.5">
          {icon}
        </div>
        <div>
          <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
          {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function PerformanceTab() {
  const [data, setData]       = useState<PerformanceMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  useErrorToast(error);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [unusedIndexOpen, setUnusedIndexOpen] = useState(false);
  const [waitingQueriesOpen, setWaitingQueriesOpen] = useState(false);
  const [longRunningOpen, setLongRunningOpen] = useState(false);
  const [cacheHitOpen, setCacheHitOpen] = useState(false);
  const [seqScanOpen, setSeqScanOpen] = useState(false);
  const [deadTupleOpen, setDeadTupleOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await fetchPerformanceMetrics();
      setData(d);
      setLastRefresh(new Date());
      setError('');
    } catch {
      setError('Metrikler yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [autoRefresh, load]);

  if (loading) return <div className="flex items-center justify-center h-60 text-gray-400 text-sm">Yükleniyor...</div>;
  if (error)   return <LoadRetry />;
  if (!data)   return null;

  const { connections: conn, cache, temp_files, table_health: tbl,
          bgwriter, replication: repl, locks, database: db, slow_queries } = data;

  // Derived
  const connPct      = Math.round((conn.total / conn.max_connections) * 100);
  const cacheHitPct  = pct(cache.blks_hit, cache.blks_read);
  const seqScanPct   = pct(tbl.seq_scan, tbl.idx_scan);
  const deadTuplePct = pct(tbl.dead_tuples, tbl.live_tuples);
  const commitPct    = pct(db.xact_commit, db.xact_rollback);
  const cpReqPct     = pct(bgwriter.checkpoints_req, bgwriter.checkpoints_timed);

  return (
    <>
    {unusedIndexOpen && <UnusedIndexModal onClose={() => setUnusedIndexOpen(false)} />}
    {waitingQueriesOpen && <WaitingQueriesModal onClose={() => setWaitingQueriesOpen(false)} />}
    {longRunningOpen && <LongRunningQueriesModal onClose={() => setLongRunningOpen(false)} />}
    {cacheHitOpen && <CacheHitModal onClose={() => setCacheHitOpen(false)} />}
    {seqScanOpen && <SeqScanModal onClose={() => setSeqScanOpen(false)} />}
    {deadTupleOpen && <DeadTupleModal onClose={() => setDeadTupleOpen(false)} />}

    <div className="flex flex-col gap-4 pb-4">

      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="text-xs text-gray-400">
          {lastRefresh && <span>Son güncelleme: {lastRefresh.toLocaleTimeString('tr-TR')}</span>}
        </div>
        <button
          onClick={() => { setLoading(true); load(); }}
          className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm hover:bg-gray-50"
        >
          <RefreshCw size={14} /> Yenile
        </button>
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <div
            onClick={() => setAutoRefresh(p => !p)}
            className={`relative w-10 h-5 rounded-full transition-colors ${autoRefresh ? 'bg-blue-500' : 'bg-gray-300'}`}
          >
            <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${autoRefresh ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </div>
          <span className={autoRefresh ? 'text-blue-600 font-medium' : 'text-gray-500'}>Otomatik (5s)</span>
        </label>
      </div>

      <Panel
        title="Database"
        icon={<Database size={14} />}
        subtitle="PostgreSQL tarafındaki bağlantı, sorgu, tablo, I/O ve replikasyon metrikleri"
      >
        <Section title="Bağlantı & Oturum" icon={<Users size={12} />}>
          <MetricCard
            icon={<Users size={14} />}
            label="Aktif Bağlantı"
            value={`${conn.total} / ${conn.max_connections}`}
            sub={`%${connPct} kullanım`}
            status={connPct >= 90 ? 'error' : connPct >= 70 ? 'warn' : 'ok'}
            progress={connPct}
          />
          <MetricCard
            icon={<Activity size={14} />}
            label="Aktif Sorgu"
            value={conn.active}
            sub={`${conn.idle} idle, ${conn.idle_in_tx} idle in tx`}
            status={conn.idle_in_tx > 5 ? 'warn' : 'muted'}
          />
          <MetricCard
            icon={<Clock size={14} />}
            label="Bekleyen"
            value={conn.waiting}
            sub="Lock bekleyen bağlantı"
            status={conn.waiting > 0 ? 'warn' : 'ok'}
            action={
              <button
                onClick={e => { e.stopPropagation(); setWaitingQueriesOpen(true); }}
                className="rounded p-0.5 hover:bg-amber-100 text-gray-400 hover:text-amber-600 transition-colors"
                title="Bekleyen sorguları göster"
              >
                <Table2 size={13} />
              </button>
            }
          />
          <MetricCard
            icon={<AlertTriangle size={14} />}
            label="Uzun Çalışan Sorgu"
            value={conn.long_running_count}
            sub={conn.long_running_count > 0 ? `Max ${conn.long_running_max_s.toFixed(0)}s` : '>5s süren yok'}
            status={conn.long_running_count > 5 ? 'error' : conn.long_running_count > 0 ? 'warn' : 'ok'}
            action={
              <button
                onClick={e => { e.stopPropagation(); setLongRunningOpen(true); }}
                className="rounded p-0.5 hover:bg-rose-100 text-gray-400 hover:text-rose-600 transition-colors"
                title="Uzun çalışan sorguları göster"
              >
                <Table2 size={13} />
              </button>
            }
          />
        </Section>

        <Section title="Sorgu Performansı" icon={<Zap size={12} />}>
          <MetricCard
            icon={<BarChart3 size={14} />}
            label="Cache Hit Oranı"
            value={cacheHitPct != null ? `%${cacheHitPct}` : '—'}
            sub={`${fmt(cache.blks_hit)} hit / ${fmt(cache.blks_read)} disk`}
            status={cacheHitPct == null ? 'muted' : cacheHitPct >= 95 ? 'ok' : cacheHitPct >= 80 ? 'warn' : 'error'}
            progress={cacheHitPct ?? undefined}
            action={
              <button
                onClick={e => { e.stopPropagation(); setCacheHitOpen(true); }}
                className="rounded p-0.5 hover:bg-blue-100 text-gray-400 hover:text-blue-600 transition-colors"
                title="Tablo bazında cache hit detayı"
              >
                <Table2 size={13} />
              </button>
            }
          />
          <MetricCard
            icon={<FileX size={14} />}
            label="Temp Dosya"
            value={fmt(temp_files.count)}
            sub={temp_files.bytes > 0 ? fmtBytes(temp_files.bytes) : 'work_mem yeterli'}
            status={temp_files.count > 100 ? 'error' : temp_files.count > 10 ? 'warn' : 'ok'}
          />
          <MetricCard
            icon={<TrendingUp size={14} />}
            label="Commit / Rollback"
            value={commitPct != null ? `%${commitPct}` : '—'}
            sub={`${fmt(db.xact_commit)} commit, ${fmt(db.xact_rollback)} rollback`}
            status={commitPct == null ? 'muted' : commitPct >= 95 ? 'ok' : commitPct >= 80 ? 'warn' : 'error'}
          />
          <MetricCard
            icon={<Database size={14} />}
            label="Veritabanı Boyutu"
            value={db.db_size}
            status="info"
          />
        </Section>

        <Section title="Tablo & Index Sağlığı" icon={<BarChart3 size={12} />}>
          <MetricCard
            icon={<Activity size={14} />}
            label="Sequential Scan %"
            value={seqScanPct != null ? `%${seqScanPct}` : '—'}
            sub={`${fmt(tbl.seq_scan)} seq / ${fmt(tbl.idx_scan)} idx`}
            status={seqScanPct == null ? 'muted' : seqScanPct >= 50 ? 'error' : seqScanPct >= 20 ? 'warn' : 'ok'}
            progress={seqScanPct ?? undefined}
            action={
              <button
                onClick={e => { e.stopPropagation(); setSeqScanOpen(true); }}
                className="rounded p-0.5 hover:bg-rose-100 text-gray-400 hover:text-rose-600 transition-colors"
                title="Tablo bazında seq scan detayı"
              >
                <Table2 size={13} />
              </button>
            }
          />
          <MetricCard
            icon={<AlertTriangle size={14} />}
            label="Dead Tuple Oranı"
            value={deadTuplePct != null ? `%${deadTuplePct}` : '—'}
            sub={`${fmt(tbl.dead_tuples)} ölü / ${fmt(tbl.live_tuples)} canlı`}
            status={deadTuplePct == null ? 'muted' : deadTuplePct >= 20 ? 'error' : deadTuplePct >= 10 ? 'warn' : 'ok'}
            progress={deadTuplePct ?? undefined}
            action={
              <button
                onClick={e => { e.stopPropagation(); setDeadTupleOpen(true); }}
                className="rounded p-0.5 hover:bg-orange-100 text-gray-400 hover:text-orange-600 transition-colors"
                title="Tablo bazında dead tuple detayı"
              >
                <Table2 size={13} />
              </button>
            }
          />
          <MetricCard
            icon={<Zap size={14} />}
            label="Kullanılmayan Index"
            value={tbl.unused_indexes}
            sub="idx_scan = 0 olan indexler"
            status={tbl.unused_indexes > 10 ? 'warn' : tbl.unused_indexes > 0 ? 'info' : 'ok'}
            action={tbl.unused_indexes > 0 ? (
              <button
                onClick={e => { e.stopPropagation(); setUnusedIndexOpen(true); }}
                className="rounded p-0.5 hover:bg-white/60 transition-colors text-gray-400 hover:text-amber-600"
                title="Detayları gör"
              >
                <Table2 size={12} />
              </button>
            ) : undefined}
          />
          <MetricCard
            icon={<TrendingUp size={14} />}
            label="Toplam Satır"
            value={fmt(tbl.live_tuples)}
            sub={`+${fmt(tbl.tup_inserted)} ekl / ${fmt(tbl.tup_updated)} güncl / ${fmt(tbl.tup_deleted)} sil`}
            status="muted"
          />
        </Section>

        <Section title="I/O & Disk (BGWriter)" icon={<HardDrive size={12} />}>
          <MetricCard
            icon={<CheckCircle size={14} />}
            label="Checkpoint (Planlı)"
            value={fmt(bgwriter.checkpoints_timed)}
            sub={`${fmt(bgwriter.checkpoints_req)} zorunlu`}
            status={cpReqPct != null && cpReqPct >= 30 ? 'warn' : 'ok'}
          />
          <MetricCard
            icon={<AlertTriangle size={14} />}
            label="Checkpoint Yazma"
            value={`${(bgwriter.checkpoint_write_ms / 1000).toFixed(1)}s`}
            sub={`Sync: ${(bgwriter.checkpoint_sync_ms / 1000).toFixed(1)}s`}
            status={bgwriter.checkpoint_write_ms > 30000 ? 'warn' : 'muted'}
          />
          <MetricCard
            icon={<HardDrive size={14} />}
            label="Buffer Yazma"
            value={fmt(bgwriter.buffers_backend)}
            sub={`Backend fsync: ${fmt(bgwriter.buffers_backend_fsync)}`}
            status={bgwriter.buffers_backend_fsync > 0 ? 'warn' : 'ok'}
          />
          <MetricCard
            icon={<BarChart3 size={14} />}
            label="Buffer Tahsis"
            value={fmt(bgwriter.buffers_alloc)}
            sub={`Checkpoint: ${fmt(bgwriter.buffers_checkpoint)}, Clean: ${fmt(bgwriter.buffers_clean)}`}
            status="muted"
          />
        </Section>

        <Section title="Lock & Wait" icon={<Lock size={12} />}>
          <MetricCard
            icon={<Lock size={14} />}
            label="Bloklanmış Sorgu"
            value={locks.blocked_queries}
            sub="Lock bekleyen aktif sorgu"
            status={locks.blocked_queries > 5 ? 'error' : locks.blocked_queries > 0 ? 'warn' : 'ok'}
          />
          <MetricCard
            icon={<AlertTriangle size={14} />}
            label="Deadlock"
            value={locks.deadlocks}
            sub={`${locks.conflicts} çakışma`}
            status={locks.deadlocks > 0 ? 'error' : 'ok'}
          />
        </Section>

        <Section title="Replikasyon" icon={<GitBranch size={12} />}>
          {repl.replica_count === 0 ? (
            <div className="col-span-full">
              <MetricCard
                icon={<GitBranch size={14} />}
                label="Replikasyon"
                value="Replica yok"
                sub="pg_stat_replication boş"
                status="muted"
              />
            </div>
          ) : (
            <>
              <MetricCard
                icon={<GitBranch size={14} />}
                label="Replica Sayısı"
                value={repl.replica_count}
                status="ok"
              />
              <MetricCard
                icon={<Clock size={14} />}
                label="Write Lag"
                value={repl.max_write_lag ?? '—'}
                status={repl.max_write_lag ? 'warn' : 'ok'}
              />
              <MetricCard
                icon={<Clock size={14} />}
                label="Replay Lag"
                value={repl.max_replay_lag ?? '—'}
                sub={repl.max_replay_lag_bytes > 0 ? fmtBytes(repl.max_replay_lag_bytes) : undefined}
                status={repl.max_replay_lag_bytes > 50 * 1024 * 1024 ? 'error' : repl.max_replay_lag ? 'warn' : 'ok'}
              />
            </>
          )}
        </Section>

        {slow_queries && slow_queries.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-600 border-b pb-1.5">
              <Clock size={12} />
              Yavaş Sorgular (pg_stat_statements — Ort. süreye göre)
            </div>
            <div className="rounded-xl border border-gray-100 overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 uppercase tracking-wide text-left">
                    <th className="px-3 py-2">Sorgu</th>
                    <th className="px-3 py-2 text-right w-20">Çalışma</th>
                    <th className="px-3 py-2 text-right w-24">Ort. Süre</th>
                    <th className="px-3 py-2 text-right w-24">Toplam</th>
                    <th className="px-3 py-2 text-right w-20">Satır</th>
                    <th className="px-3 py-2 text-right w-24">Cache Hit %</th>
                  </tr>
                </thead>
                <tbody>
                  {slow_queries.map((q, i) => {
                    const hit = q.shared_blks_hit + q.shared_blks_read;
                    const hitPct = hit > 0 ? Math.round((q.shared_blks_hit / hit) * 100) : null;
                    const cls = q.mean_exec_time_ms > 1000 ? 'text-rose-600 font-bold'
                      : q.mean_exec_time_ms > 300 ? 'text-amber-600 font-semibold' : 'text-gray-700';
                    return (
                      <tr key={i} className="border-t hover:bg-gray-50">
                        <td className="px-3 py-2 font-mono text-gray-600 max-w-xs">
                          <span title={q.query}>{q.query.slice(0, 100)}{q.query.length > 100 ? '…' : ''}</span>
                        </td>
                        <td className="px-3 py-2 text-right text-gray-500">{q.calls.toLocaleString('tr-TR')}</td>
                        <td className={`px-3 py-2 text-right ${cls}`}>{q.mean_exec_time_ms} ms</td>
                        <td className="px-3 py-2 text-right text-gray-500">{(q.total_exec_time_ms / 1000).toFixed(1)}s</td>
                        <td className="px-3 py-2 text-right text-gray-500">{fmt(q.rows)}</td>
                        <td className="px-3 py-2 text-right">
                          {hitPct != null
                            ? <span className={hitPct >= 95 ? 'text-emerald-600' : hitPct >= 80 ? 'text-amber-600' : 'text-rose-600 font-bold'}>{hitPct}%</span>
                            : <span className="text-gray-300">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {slow_queries === null && (
          <div className="rounded-xl border border-dashed border-gray-200 p-4 text-center text-sm text-gray-400">
            pg_stat_statements eklentisi aktif değil — yavaş sorgu analizi kullanılamıyor.
          </div>
        )}
      </Panel>
    </div>
    </>
  );
}
